/**
 * Product Card Title Horizontal Scroll
 * Enables horizontal scrolling for product titles that overflow
 * - Desktop/Tablet: Activates on hover
 * - Mobile: Activates when element is in viewport
 */

class ProductCardTitleScroll {
  constructor() {
    this.titles = [];
    this.observer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupTitles());
    } else {
      this.setupTitles();
    }
  }

  setupTitles() {
    // Find all product card titles
    const titleElements = document.querySelectorAll('.xo-product-card__title');
    
    titleElements.forEach(title => {
      this.setupTitle(title);
    });

    // Setup intersection observer for mobile
    if (window.innerWidth < 768) {
      this.setupIntersectionObserver();
    }

    // Re-setup on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  setupTitle(titleElement) {
    const link = titleElement.querySelector('a');
    if (!link) return;

    // Check if text overflows
    const doesOverflow = this.checkOverflow(titleElement, link);
    
    if (doesOverflow) {
      // Enable scrolling
      titleElement.setAttribute('data-scroll-enabled', 'true');
      link.setAttribute('data-overflow', 'true');
      
      // Calculate scroll duration based on text length
      const textLength = link.textContent.length;
      const duration = Math.max(5, textLength * 0.15); // Minimum 5s, scales with length
      titleElement.style.setProperty('--scroll-duration', `${duration}s`);
      
      // Calculate visible width for smooth scrolling
      const visibleWidth = titleElement.offsetWidth;
      titleElement.style.setProperty('--visible-width', `${visibleWidth}px`);
      
      // Store reference
      this.titles.push({
        element: titleElement,
        link: link,
        visibleWidth: visibleWidth
      });
    } else {
      // No overflow, disable scrolling
      titleElement.removeAttribute('data-scroll-enabled');
      link.removeAttribute('data-overflow');
    }
  }

  checkOverflow(container, element) {
    // Temporarily remove any transformations to get accurate width
    const originalTransform = element.style.transform;
    element.style.transform = 'none';
    
    const containerWidth = container.offsetWidth;
    const elementWidth = element.scrollWidth;
    
    // Restore transform
    element.style.transform = originalTransform;
    
    return elementWidth > containerWidth;
  }

  setupIntersectionObserver() {
    // Clean up existing observer
    if (this.observer) {
      this.observer.disconnect();
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Trigger when 50% visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const titleElement = entry.target;
        if (entry.isIntersecting) {
          titleElement.setAttribute('data-in-view', 'true');
        } else {
          titleElement.setAttribute('data-in-view', 'false');
        }
      });
    }, options);

    // Observe all titles
    this.titles.forEach(({ element }) => {
      this.observer.observe(element);
    });
  }

  handleResize() {
    // Clear existing titles
    if (this.observer) {
      this.observer.disconnect();
    }
    this.titles = [];

    // Re-setup
    this.setupTitles();
  }

  // Public method to refresh titles (useful for dynamic content)
  refresh() {
    this.handleResize();
  }
}

// Initialize
const productCardTitleScroll = new ProductCardTitleScroll();

// Expose globally for manual refresh if needed
if (typeof window !== 'undefined') {
  window.productCardTitleScroll = productCardTitleScroll;
}

// Auto-refresh when new products are loaded (e.g., after Ajax)
if (typeof window !== 'undefined') {
  // Listen for common Shopify events
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => productCardTitleScroll.refresh(), 100);
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => productCardTitleScroll.refresh(), 100);
  });

  // Listen for custom product load events
  document.addEventListener('product-card:loaded', () => {
    setTimeout(() => productCardTitleScroll.refresh(), 100);
  });
}
